'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';

interface AuthForm {
  password: string;
}

// Mock admin credentials — Backend integration point: POST /api/admin/auth
const ADMIN_PASSWORD = 'NovaAdmin@2026';

interface AdminAuthGateProps {
  onAuthenticated: () => void;
}

export default function AdminAuthGate({ onAuthenticated }: AdminAuthGateProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, formState: { errors }, setError } = useForm<AuthForm>();

  const onSubmit = async (data: AuthForm) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 600));
    if (data.password === ADMIN_PASSWORD) {
      toast.success('Welcome to Admin Panel!');
      onAuthenticated();
    } else {
      setError('password', { message: 'Incorrect password — use the demo credentials below.' });
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 qr-dot-pattern">
      <div className="w-full max-w-sm animate-scale-in">
        {/* Card */}
        <div className="bg-card rounded-3xl border border-border shadow-xl-custom p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="flex items-center gap-2 mb-3">
              <AppLogo size={40} />
              <span className="font-bold text-foreground" style={{ fontSize: '18px' }}>Nova QR Studio</span>
            </div>
            <div className="w-12 h-12 rounded-2xl gradient-primary flex items-center justify-center shadow-blue mb-3">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 2L19.5 6.5V11C19.5 16 15.5 20 11 21C6.5 20 2.5 16 2.5 11V6.5L11 2Z" />
                <path d="M8 11L10 13L14 9" />
              </svg>
            </div>
            <h1 className="font-bold text-foreground" style={{ fontSize: '20px' }}>Admin Panel</h1>
            <p className="text-muted-foreground mt-1" style={{ fontSize: '13px' }}>Password-protected access only</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block font-semibold text-foreground" style={{ fontSize: '13px' }}>Admin Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input-field pr-12"
                  placeholder="Enter admin password"
                  autoComplete="current-password"
                  {...register('password', { required: 'Password is required.' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                      <path d="M1 8S3.5 3 8 3s7 5 7 5-2.5 5-7 5-7-5-7-5Z" />
                      <circle cx="8" cy="8" r="2" />
                      <line x1="2" y1="2" x2="14" y2="14" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                      <path d="M1 8S3.5 3 8 3s7 5 7 5-2.5 5-7 5-7-5-7-5Z" />
                      <circle cx="8" cy="8" r="2" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-rose-500" style={{ fontSize: '12px', fontWeight: 500 }}>{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ fontSize: '14px' }}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" strokeDasharray="19" strokeDashoffset="7" />
                  </svg>
                  Verifying...
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                    <rect x="2.5" y="6.5" width="10" height="7.5" rx="1.5" />
                    <path d="M5 6.5V4a2.5 2.5 0 0 1 5 0v2.5" />
                  </svg>
                  Access Admin Panel
                </>
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-5 gradient-blue-soft rounded-2xl p-4 border border-border">
            <p className="font-semibold text-primary mb-2" style={{ fontSize: '12px' }}>Demo Credentials</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground" style={{ fontSize: '11px' }}>Password</p>
                <p className="font-mono font-semibold text-foreground" style={{ fontSize: '13px' }}>NovaAdmin@2026</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(ADMIN_PASSWORD);
                  toast.success('Password copied!');
                }}
                className="p-2 rounded-xl text-primary hover:bg-primary/10 transition-colors"
                aria-label="Copy password"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                  <rect x="4" y="4" width="8.5" height="8.5" rx="1.5" />
                  <path d="M2 10V2.5A.5.5 0 0 1 2.5 2H10" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-muted-foreground mt-4" style={{ fontSize: '12px' }}>
          © NovaMax Studios. All Rights Reserved.
        </p>
      </div>
    </div>
  );
}